"use client";
import {
  Auth,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
} from "firebase/auth";
import { HttpStatus } from "@/app/lib/models/ResponseFormatModel";
import FirebaseClientService from "@/app/lib/firebase/FirebaseClient.client";
import logger from "@/lib/logger";

export interface SignInData {
  code: number;
  token?: string;
  email?: string;
  full_name?: string;
}

class FirebaseAuthServiceClient {
  private static fbAuth: Auth;
  private static instance: FirebaseAuthServiceClient;

  constructor() {
    FirebaseClientService.getFirebaseClient()
      .then((client) => {
        if (client) {
          FirebaseAuthServiceClient.fbAuth = client.getFirebaseAuth();
        }
      })
      .catch((error) => {
        logger.error("Error in getting Firebase client", {
          error: String(error),
        });
      });
  }

  public static async getInstance(): Promise<FirebaseAuthServiceClient> {
    if (!FirebaseAuthServiceClient.instance) {
      FirebaseAuthServiceClient.instance = new FirebaseAuthServiceClient();
    }
    return FirebaseAuthServiceClient.instance;
  }

  public getFirebaseAuthClient(): Auth {
    return FirebaseAuthServiceClient.fbAuth;
  }

  /**
   * Create a new user with email and password. Unused at the moment but can be used for future reference.
   * @param email : string email id of the user
   * @param password : string password of the user
   */
  public async doCreateUserWithEmailAndPassword(
    email: string,
    password: string,
  ) {
    return createUserWithEmailAndPassword(
      FirebaseAuthServiceClient.fbAuth,
      email,
      password,
    );
  }

  /**
   * Sign in with email and password. Unused at the moment but can be used for future reference.
   * @param email : string email id of the user
   * @param password : string password of the user
   */
  public async doSignInWithEmailAndPassword(email: string, password: string) {
    const result = await signInWithEmailAndPassword(
      FirebaseAuthServiceClient.fbAuth,
      email,
      password,
    );
    const token = await result.user.getIdToken(false);
    return {
      code: HttpStatus.OK,
      email: result.user.email,
      token: token,
      full_name: result.user.displayName,
    } as SignInData;
  }

  public async doSignInWithGoogle(): Promise<SignInData> {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(
        FirebaseAuthServiceClient.fbAuth,
        provider,
      );
      const token = await result.user.getIdToken(false);
      return {
        code: HttpStatus.OK,
        email: result.user.email,
        token: token,
        full_name: result.user.displayName,
      } as SignInData;
    } catch (error) {
      if ((error.code as string).includes("unauthorized-domain")) {
        throw new Error("unauthorized-domain");
      } else {
        throw new Error(JSON.stringify(error));
      }
    }
  }

  public async doSignInWithMicrosoft(): Promise<SignInData> {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ tenant: "common" });

    try {
      const result = await signInWithPopup(
        FirebaseAuthServiceClient.fbAuth,
        provider,
      );
      const credential = OAuthProvider.credentialFromResult(result);

      if (credential) {
        const idToken = credential.idToken;
        return {
          code: HttpStatus.OK,
          email: result.user.email,
          token: idToken,
          full_name: result.user.displayName,
        } as SignInData;
      } else {
        return { code: 401 } as SignInData;
      }
    } catch (error) {
      if ((error.code as string).includes("unauthorized-domain")) {
        throw new Error("unauthorized-domain");
      } else {
        throw new Error(JSON.stringify(error));
      }
    }
  }

  public doSignOut() {
    return FirebaseAuthServiceClient.fbAuth.signOut();
  }

  public checkAuthToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      onAuthStateChanged(FirebaseAuthServiceClient.fbAuth, (user) => {
        if (user) {
          user
            .getIdToken(true)
            .then((token) => {
              resolve(token);
            })
            .catch((error) => {
              reject(error);
            });
        } else {
          reject(new Error("No user is signed in."));
        }
      });
    });
  }
}

/** Waits briefly for Firebase to restore the signed-in user (e.g. after a full page reload). */
export function waitForFirebaseAuthUser(
  auth: Auth,
  timeoutMs = 4000,
): Promise<User | null> {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (user: User | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(user);
    };

    const timer = setTimeout(() => finish(auth.currentUser), timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, (user) => finish(user));
  });
}

export default FirebaseAuthServiceClient;
